import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAuth } from './AuthContext';
import API_BASE from '../utils/apiBase';
import {
  canonicalUnreadTotal,
  communicationDraftKey,
  communicationsWorkspaceIdentity,
  sortRecruitmentThreads,
} from '../lib/communicationsWorkspace';

const CommunicationsWorkspaceContext = createContext(null);
const API = API_BASE;

export function CommunicationsWorkspaceProvider({ children }) {
  const { user, token, isManagerOrAdmin } = useAuth();
  const canUse = Boolean(token && isManagerOrAdmin?.());
  const workspaceKey = communicationsWorkspaceIdentity(user);
  const userKey = String(user?.user_id || user?.id || user?.email || 'user');

  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState(null);
  const [threads, setThreads] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [drafts, setDrafts] = useState({});

  const refreshThreads = useCallback(async () => {
    if (!canUse || !token || typeof fetch !== 'function') return;
    setThreadsLoading(true);
    try {
      const response = await fetch(`${API}/portal-inbox/threads?department=recruitment&status=all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Could not load recruitment conversations');
      const payload = await response.json();
      setThreads(sortRecruitmentThreads(payload?.threads || []));
      setUnreadTotal(canonicalUnreadTotal(payload));
    } catch {
      // Keep the previous list/badge instead of presenting a false zero.
    } finally {
      setThreadsLoading(false);
    }
  }, [canUse, token]);

  useEffect(() => {
    setOpen(false);
    setSelection(null);
    setThreads([]);
    setUnreadTotal(0);
    setDrafts({});
  }, [workspaceKey, userKey, token]);

  useEffect(() => {
    if (!canUse) return undefined;
    refreshThreads();
    const id = window.setInterval(refreshThreads, 5000);
    const handleUpdated = () => refreshThreads();
    window.addEventListener('careops:recruitment-communication-updated', handleUpdated);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('careops:recruitment-communication-updated', handleUpdated);
    };
  }, [canUse, refreshThreads]);

  const openInbox = useCallback(() => {
    if (!canUse) return;
    setSelection(null);
    setOpen(true);
    refreshThreads();
  }, [canUse, refreshThreads]);

  const openApplicant = useCallback(({ employeeId, threadId = null } = {}) => {
    const safeEmployeeId = String(employeeId || '').trim();
    const safeThreadId = String(threadId || '').trim();
    if (!canUse || (!safeEmployeeId && !safeThreadId)) return;
    setSelection({
      employeeId: safeEmployeeId || null,
      threadId: safeThreadId || null,
    });
    setOpen(true);
  }, [canUse]);

  const selectConversation = useCallback((next) => {
    if (!next) {
      setSelection(null);
      return;
    }
    setSelection((current) => {
      const candidate = {
        employeeId: String(next.employeeId || current?.employeeId || '').trim() || null,
        threadId: String(next.threadId || current?.threadId || '').trim() || null,
      };
      if (
        current?.employeeId === candidate.employeeId
        && current?.threadId === candidate.threadId
      ) return current;
      return candidate;
    });
  }, []);

  const ensureDraft = useCallback((key, defaults = {}) => {
    if (!key || key === 'global') return;
    setDrafts((current) => {
      if (current[key]) return current;
      return {
        ...current,
        [key]: {
          subject: '',
          body: '',
          channel: defaults.channel === 'whatsapp' ? 'whatsapp' : 'email',
        },
      };
    });
  }, []);

  const updateDraft = useCallback((key, value) => {
    if (!key || key === 'global') return;
    setDrafts((current) => ({
      ...current,
      [key]: {
        subject: String(value?.subject || ''),
        body: String(value?.body || ''),
        channel: value?.channel === 'whatsapp' ? 'whatsapp' : 'email',
      },
    }));
  }, []);

  const value = useMemo(() => ({
    canUse,
    open,
    setOpen,
    selection,
    threads,
    unreadTotal,
    threadsLoading,
    refreshThreads,
    openInbox,
    openApplicant,
    selectConversation,
    drafts,
    draftKey: communicationDraftKey(selection || {}),
    ensureDraft,
    updateDraft,
  }), [
    canUse,
    open,
    selection,
    threads,
    unreadTotal,
    threadsLoading,
    refreshThreads,
    openInbox,
    openApplicant,
    selectConversation,
    drafts,
    ensureDraft,
    updateDraft,
  ]);

  return (
    <CommunicationsWorkspaceContext.Provider value={value}>
      {children}
    </CommunicationsWorkspaceContext.Provider>
  );
}

export function useCommunicationsWorkspace() {
  const value = useContext(CommunicationsWorkspaceContext);
  if (!value) {
    throw new Error('useCommunicationsWorkspace must be used inside CommunicationsWorkspaceProvider');
  }
  return value;
}
